import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { db } from "../firebase";
import { ref, push, onValue, remove, update, get, set } from "firebase/database";

export default function Teachers() {
  const navigate = useNavigate();
  const location = useLocation();
  const [teachers, setTeachers] = useState({});
  const [filteredTeachers, setFilteredTeachers] = useState({});
  const [searchTerm, setSearchTerm] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [isOffline, setIsOffline] = useState(!navigator.onLine);
  
  // Form states
  const [name, setName] = useState("");
  const [mobile, setMobile] = useState("");
  const [teacherId, setTeacherId] = useState("");
  const [password, setPassword] = useState("");
  const [isEditing, setIsEditing] = useState(false);
  const [editId, setEditId] = useState(null);
  
  // Validation errors
  const [errors, setErrors] = useState({});
  
  // Success/Error messages
  const [message, setMessage] = useState({ type: "", text: "" });

  // Cache keys
  const CACHE_KEY = "library_teachers_cache";
  const CACHE_TIMESTAMP_KEY = "library_teachers_timestamp";
  const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

  // Bottom navigation items
const bottomNavItems = [
    { name: "Dashboard", path: "/admin-dashboard", icon: "🏠", activeIcon: "🏠" },
    { name: "Books", path: "/books", icon: "📚", activeIcon: "📚" },
    { name: "Teachers", path: "/teachers", icon: "👩‍🏫", activeIcon: "👩‍🏫" },
    { name: "Issue/Return", path: "/manage-books", icon: "🔄", activeIcon: "🔄" },
    { name: "Reports", path: "/reports", icon: "📊", activeIcon: "📊" },
    { name: "Settings", path: "/settings", icon: "⚙️", activeIcon: "⚙️" }
  ];
  // Generate unique 4-digit teacher ID
  const generateTeacherId = async () => {
    const teachersRef = ref(db, "users");
    const snapshot = await get(teachersRef);
    const allUsers = snapshot.val() || {};
    
    // Get all existing teacher IDs
    const existingIds = [];
    Object.values(allUsers).forEach(user => {
      if (user.role === "teacher" && user.teacherId) {
        existingIds.push(parseInt(user.teacherId));
      }
    });
    
    // Find the next available ID starting from 1111
    let newId = 1111;
    while (existingIds.includes(newId)) {
      newId++;
    }
    return newId.toString();
  };

  // Load from cache
  const loadFromCache = () => {
    const cachedTeachers = localStorage.getItem(CACHE_KEY);
    const cacheTimestamp = localStorage.getItem(CACHE_TIMESTAMP_KEY);
    
    if (cachedTeachers && cacheTimestamp) {
      const isExpired = Date.now() - parseInt(cacheTimestamp) > CACHE_DURATION;
      if (!isExpired) {
        const teacherData = JSON.parse(cachedTeachers);
        setTeachers(teacherData);
        setFilteredTeachers(teacherData);
        setIsLoading(false);
        return true;
      }
    }
    return false;
  };

  // Save to cache
  const saveToCache = (teacherData) => {
    localStorage.setItem(CACHE_KEY, JSON.stringify(teacherData));
    localStorage.setItem(CACHE_TIMESTAMP_KEY, Date.now().toString());
  };

  // Fetch teachers from Firebase with caching
  useEffect(() => {
    const hasCache = loadFromCache();

    const handleOnline = () => {
      setIsOffline(false);
      setMessage({ type: "success", text: "Back online! Syncing data..." });
      setTimeout(() => setMessage({ type: "", text: "" }), 3000);
    };
    
    const handleOffline = () => {
      setIsOffline(true);
      setMessage({ type: "error", text: "You are offline. Showing cached data." });
      setTimeout(() => setMessage({ type: "", text: "" }), 3000);
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    const usersRef = ref(db, "users");
    const unsubscribe = onValue(usersRef, 
      (snapshot) => {
        const data = snapshot.val() || {};
        // Filter only teachers
        const teacherData = {};
        Object.entries(data).forEach(([id, user]) => {
          if (user.role === "teacher") {
            teacherData[id] = user;
          }
        });
        setTeachers(teacherData);
        setFilteredTeachers(teacherData);
        saveToCache(teacherData);
        setIsLoading(false);
        setMessage({ type: "", text: "" });
      },
      (error) => {
        console.error("Firebase error:", error);
        if (!hasCache) {
          setMessage({ type: "error", text: "Failed to load teachers. Please refresh." });
        }
        setIsLoading(false);
      }
    );
    
    return () => {
      unsubscribe();
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // Filter teachers based on search term
  useEffect(() => {
    if (searchTerm.trim() === "") {
      setFilteredTeachers(teachers);
    } else {
      const filtered = {};
      Object.entries(teachers).forEach(([id, teacher]) => {
        if (
          teacher.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          teacher.mobile?.includes(searchTerm) ||
          teacher.teacherId?.includes(searchTerm)
        ) {
          filtered[id] = teacher;
        }
      });
      setFilteredTeachers(filtered);
    }
  }, [searchTerm, teachers]);

  // Validation function
  const validateForm = () => {
    const newErrors = {};
    
    if (!name.trim()) {
      newErrors.name = "Name is required";
    } else if (name.trim().length < 2) {
      newErrors.name = "Name must be at least 2 characters";
    }
    
    // Mobile is optional, but validate if provided
    if (mobile.trim() && !/^[0-9]{10}$/.test(mobile.trim())) {
      newErrors.mobile = "Please enter a valid 10-digit mobile number";
    }
    
    // Teacher ID validation (if provided, must be 4 digits)
    if (teacherId.trim()) {
      if (!/^[0-9]{4}$/.test(teacherId.trim())) {
        newErrors.teacherId = "Teacher ID must be exactly 4 digits";
      }
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Clear form
  const clearForm = () => {
    setName("");
    setMobile("");
    setTeacherId("");
    setPassword("");
    setIsEditing(false);
    setEditId(null);
    setErrors({});
    setMessage({ type: "", text: "" });
  };

  // Check if teacher ID exists
  const isTeacherIdExists = async (id, excludeUserId = null) => {
    const teachersRef = ref(db, "users");
    const snapshot = await get(teachersRef);
    const allUsers = snapshot.val() || {};
    
    return Object.entries(allUsers).some(([userId, user]) => {
      if (excludeUserId && userId === excludeUserId) return false;
      return user.role === "teacher" && user.teacherId === id;
    });
  };

  // ADD TEACHER
  const addTeacher = async () => {
    if (!validateForm()) return;
    
    setIsLoading(true);
    
    try {
      let finalTeacherId = teacherId.trim();
      let finalPassword = password.trim();
      
      // Auto-generate teacher ID if not provided
      if (!finalTeacherId) {
        finalTeacherId = await generateTeacherId();
      } else {
        // Check if provided ID already exists
        const exists = await isTeacherIdExists(finalTeacherId);
        if (exists) {
          setErrors({ teacherId: "This Teacher ID already exists!" });
          setIsLoading(false);
          return;
        }
      }
      
      // Set default password if not provided
      if (!finalPassword) {
        finalPassword = "teacher123";
      }
      
      const newRef = push(ref(db, "users"));
      
      await set(newRef, {
        name: name.trim(),
        mobile: mobile.trim() || "",
        teacherId: finalTeacherId,
        password: finalPassword,
        role: "teacher",
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        status: "active"
      });
      
      setMessage({ type: "success", text: `Teacher added successfully! ID: ${finalTeacherId}, Password: ${finalPassword}` });
      clearForm();
      
      // Refresh data
      const snapshot = await get(ref(db, "users"));
      const data = snapshot.val() || {};
      const teacherData = {};
      Object.entries(data).forEach(([id, user]) => {
        if (user.role === "teacher") {
          teacherData[id] = user;
        }
      });
      setTeachers(teacherData);
      saveToCache(teacherData);
      
    } catch (error) {
      setMessage({ type: "error", text: "Failed to add teacher: " + error.message });
    } finally {
      setIsLoading(false);
      setTimeout(() => setMessage({ type: "", text: "" }), 4000);
    }
  };

  // UPDATE TEACHER
  const updateTeacher = async () => {
    if (!validateForm()) return;
    
    setIsLoading(true);
    
    try {
      const updates = {
        name: name.trim(),
        mobile: mobile.trim() || "",
        updatedAt: new Date().toISOString()
      };
      
      // Update teacher ID if provided and changed
      if (teacherId.trim() && teacherId.trim() !== teachers[editId]?.teacherId) {
        const exists = await isTeacherIdExists(teacherId.trim(), editId);
        if (exists) {
          setErrors({ teacherId: "This Teacher ID already exists!" });
          setIsLoading(false);
          return;
        }
        updates.teacherId = teacherId.trim();
      }
      
      // Update password if provided
      if (password.trim()) {
        updates.password = password.trim();
      }
      
      await update(ref(db, `users/${editId}`), updates);
      
      setMessage({ type: "success", text: "Teacher updated successfully!" });
      clearForm();
      
      // Refresh data
      const snapshot = await get(ref(db, "users"));
      const data = snapshot.val() || {};
      const teacherData = {};
      Object.entries(data).forEach(([id, user]) => {
        if (user.role === "teacher") {
          teacherData[id] = user;
        }
      });
      setTeachers(teacherData);
      saveToCache(teacherData);
      
    } catch (error) {
      setMessage({ type: "error", text: "Failed to update teacher: " + error.message });
    } finally {
      setIsLoading(false);
      setTimeout(() => setMessage({ type: "", text: "" }), 3000);
    }
  };

  // DELETE TEACHER
  const deleteTeacher = async (id, teacherName) => {
    if (window.confirm(`Are you sure you want to delete teacher "${teacherName}"?`)) {
      setIsLoading(true);
      try {
        await remove(ref(db, `users/${id}`));
        setMessage({ type: "success", text: "Teacher deleted successfully!" });
        
        // Refresh data
        const snapshot = await get(ref(db, "users"));
        const data = snapshot.val() || {};
        const teacherData = {};
        Object.entries(data).forEach(([id, user]) => {
          if (user.role === "teacher") {
            teacherData[id] = user;
          }
        });
        setTeachers(teacherData);
        saveToCache(teacherData);
        
      } catch (error) {
        setMessage({ type: "error", text: "Failed to delete teacher: " + error.message });
      } finally {
        setIsLoading(false);
        setTimeout(() => setMessage({ type: "", text: "" }), 3000);
      }
    }
  };

  // EDIT TEACHER (load data into form)
  const editTeacher = (id, teacherData) => {
    setName(teacherData.name || "");
    setMobile(teacherData.mobile || "");
    setTeacherId(teacherData.teacherId || "");
    setPassword(""); // Don't populate password for security
    setIsEditing(true);
    setEditId(id);
    setErrors({});
    // Scroll to form
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (isEditing) {
      updateTeacher();
    } else {
      addTeacher();
    }
  };

  // Handle navigation
  const handleNavigation = (path) => {
    navigate(path);
  };

  // Handle logout
  const handleLogout = () => {
    if (window.confirm("Are you sure you want to logout?")) {
      localStorage.removeItem("user");
      localStorage.removeItem("adminLoggedIn");
      localStorage.removeItem("role");
      localStorage.removeItem("userName");
      navigate("/");
    }
  };

  // Get statistics
  const totalTeachers = Object.keys(teachers).length;
  const activeTeachers = Object.values(teachers).filter(t => t.status === "active").length;
  const userName = localStorage.getItem("userName") || "Admin";

  return (
    <div style={styles.container}>
      {/* Offline Banner */}
      {isOffline && (
        <div style={styles.offlineBanner}>
          ⚠️ You are offline. Showing cached data. Changes will sync when online.
        </div>
      )}

      {/* Message Alert */}
      {message.text && (
        <div style={{...styles.alert, ...(message.type === "success" ? styles.alertSuccess : styles.alertError)}}>
          {message.type === "success" ? "✅" : "❌"} {message.text}
        </div>
      )}

      {/* Header */}
      <div style={styles.header}>
        <div>
          <h1 style={styles.title}>👩‍🏫 Teacher Management</h1>
          <p style={styles.subtitle}>Manage all teachers in the library system</p>
        </div>
        <div style={styles.headerRight}>
          <div style={styles.adminProfile} onClick={() => setShowMobileMenu(!showMobileMenu)}>
            <div style={styles.adminAvatar}>
              {userName.charAt(0).toUpperCase()}
            </div>
          </div>
        </div>
      </div>

      {/* Mobile Menu Dropdown */}
      {showMobileMenu && (
        <div style={styles.mobileMenu}>
          <div style={styles.mobileMenuItem} onClick={() => { navigate("/profile"); setShowMobileMenu(false); }}>
            <span>👤</span> My Profile
          </div>
          <div style={styles.mobileMenuItem} onClick={() => { navigate("/settings"); setShowMobileMenu(false); }}>
            <span>⚙️</span> Settings
          </div>
          <div style={styles.mobileMenuItem} onClick={() => { handleLogout(); setShowMobileMenu(false); }}>
            <span>🚪</span> Logout
          </div>
        </div>
      )}

      {/* Statistics Cards */}
      <div style={styles.statsGrid}>
        <div style={styles.statCard}>
          <span style={styles.statIcon}>👩‍🏫</span>
          <div>
            <h3 style={styles.statValue}>{totalTeachers}</h3>
            <p style={styles.statLabel}>Total Teachers</p>
          </div>
        </div>
        <div style={styles.statCard}>
          <span style={styles.statIcon}>✅</span>
          <div>
            <h3 style={styles.statValue}>{activeTeachers}</h3>
            <p style={styles.statLabel}>Active Teachers</p>
          </div>
        </div>
        <div style={styles.statCard}>
          <span style={styles.statIcon}>🆔</span>
          <div>
            <h3 style={styles.statValue}>{Object.keys(teachers).length}</h3>
            <p style={styles.statLabel}>Unique IDs</p>
          </div>
        </div>
      </div>

      {/* Add/Edit Form */}
      <div style={styles.formCard}>
        <h3 style={styles.formTitle}>
          {isEditing ? "✏️ Edit Teacher" : "➕ Add New Teacher"}
        </h3>
        
        <form onSubmit={handleSubmit}>
          <div style={styles.formGroup}>
            <label style={styles.label}>Full Name *</label>
            <input
              type="text"
              placeholder="Enter teacher's full name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              style={{...styles.input, ...(errors.name ? styles.inputError : {})}}
              disabled={isLoading}
            />
            {errors.name && <span style={styles.errorText}>{errors.name}</span>}
          </div>

          <div style={styles.formGroup}>
            <label style={styles.label}>Mobile Number (Optional)</label>
            <input
              type="tel"
              placeholder="10-digit mobile number"
              value={mobile}
              onChange={(e) => setMobile(e.target.value.replace(/[^0-9]/g, "").slice(0, 10))}
              style={{...styles.input, ...(errors.mobile ? styles.inputError : {})}}
              disabled={isLoading}
            />
            {errors.mobile && <span style={styles.errorText}>{errors.mobile}</span>}
            <span style={styles.hintText}>Optional - can be added later</span>
          </div>

          <div style={styles.formGroup}>
            <label style={styles.label}>
              Teacher ID (Optional - 4 digits)
            </label>
            <input
              type="text"
              placeholder="4-digit ID (e.g., 1111) or leave empty for auto-generate"
              value={teacherId}
              onChange={(e) => setTeacherId(e.target.value.replace(/[^0-9]/g, "").slice(0, 4))}
              style={{...styles.input, ...(errors.teacherId ? styles.inputError : {})}}
              disabled={isLoading}
            />
            {errors.teacherId && <span style={styles.errorText}>{errors.teacherId}</span>}
            <span style={styles.hintText}>Auto-generates unique ID starting from 1111 if left empty</span>
          </div>

          <div style={styles.formGroup}>
            <label style={styles.label}>
              Password {!isEditing && "(Optional)"}
              {isEditing && <span style={styles.optionalText}> (Leave blank to keep current)</span>}
            </label>
            <input
              type="password"
              placeholder={isEditing ? "Enter new password (optional)" : "Leave empty for default: teacher123"}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              style={{...styles.input, ...(errors.password ? styles.inputError : {})}}
              disabled={isLoading}
            />
            {!isEditing && <span style={styles.hintText}>Default password: teacher123</span>}
            {errors.password && <span style={styles.errorText}>{errors.password}</span>}
          </div>

          <div style={styles.buttonGroup}>
            <button 
              type="submit" 
              style={{...styles.submitBtn, ...(isLoading ? styles.disabledBtn : {})}}
              disabled={isLoading}
            >
              {isLoading ? (
                <span style={styles.loader}></span>
              ) : isEditing ? (
                "✏️ Update Teacher"
              ) : (
                "➕ Add Teacher"
              )}
            </button>
            
            {isEditing && (
              <button 
                type="button" 
                onClick={clearForm}
                style={styles.cancelBtn}
              >
                Cancel
              </button>
            )}
          </div>
        </form>
      </div>

      {/* Search Bar */}
      <div style={styles.searchSection}>
        <div style={styles.searchWrapper}>
          <span style={styles.searchIcon}>🔍</span>
          <input
            type="text"
            placeholder="Search by name, mobile or teacher ID..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={styles.searchInput}
          />
          {searchTerm && (
            <button onClick={() => setSearchTerm("")} style={styles.clearBtn}>
              ✕
            </button>
          )}
        </div>
      </div>

      {/* Teachers List */}
      <div style={styles.listCard}>
        <div style={styles.listHeader}>
          <h3 style={styles.listTitle}>📋 Teacher List</h3>
          <span style={styles.listCount}>{Object.keys(filteredTeachers).length} teachers</span>
        </div>

        {isLoading && Object.keys(teachers).length === 0 ? (
          <div style={styles.loadingState}>
            <div style={styles.spinner}></div>
            <p>Loading teachers...</p>
          </div>
        ) : Object.keys(filteredTeachers).length === 0 ? (
          <div style={styles.emptyState}>
            <span style={styles.emptyIcon}>👩‍🏫</span>
            <p>No teachers found</p>
            {searchTerm && <button onClick={() => setSearchTerm("")} style={styles.clearSearchBtn}>Clear Search</button>}
          </div>
        ) : (
          <div style={styles.tableWrapper}>
            <table style={styles.table}>
              <thead>
                <tr style={styles.tableHeader}>
                  <th style={styles.th}>#</th>
                  <th style={styles.th}>Teacher ID</th>
                  <th style={styles.th}>Name</th>
                  <th style={styles.th}>Mobile</th>
                  <th style={styles.th}>Status</th>
                  <th style={styles.th}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {Object.entries(filteredTeachers).map(([id, teacher], index) => (
                  <tr key={id} style={styles.tableRow}>
                    <td style={styles.td}>{index + 1}</td>
                    <td style={styles.td}>
                      <code style={styles.teacherId}>{teacher.teacherId || "N/A"}</code>
                    </td>
                    <td style={styles.td}>
                      <div style={styles.teacherName}>
                        <span style={styles.avatar}>
                          {teacher.name?.charAt(0).toUpperCase() || "T"}
                        </span>
                        {teacher.name}
                      </div>
                    </td>
                    <td style={styles.td}>
                      {teacher.mobile ? (
                        <a href={`tel:${teacher.mobile}`} style={styles.mobileLink}>
                          📞 {teacher.mobile}
                        </a>
                      ) : (
                        <span style={styles.naText}>Not provided</span>
                      )}
                    </td>
                    <td style={styles.td}>
                      <span style={teacher.status === "active" ? styles.statusActive : styles.statusInactive}>
                        {teacher.status === "active" ? "Active" : "Inactive"}
                      </span>
                    </td>
                    <td style={styles.td}>
                      <div style={styles.actionButtons}>
                        <button
                          onClick={() => editTeacher(id, teacher)}
                          style={styles.editBtn}
                          title="Edit Teacher"
                        >
                          ✏️ Edit
                        </button>
                        <button
                          onClick={() => deleteTeacher(id, teacher.name)}
                          style={styles.deleteBtn}
                          title="Delete Teacher"
                        >
                          🗑️ Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Bottom Navigation Bar */}
      <div style={styles.bottomNav}>
        {bottomNavItems.map((item) => (
          <div
            key={item.path}
            style={{
              ...styles.bottomNavItem,
              ...(location.pathname === item.path && styles.bottomNavItemActive)
            }}
            onClick={() => handleNavigation(item.path)}
          >
            <div style={styles.bottomNavIcon}>
              {location.pathname === item.path ? item.activeIcon : item.icon}
            </div>
            <span style={styles.bottomNavLabel}>{item.name}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

const styles = {
  container: {
    maxWidth: "1200px",
    margin: "0 auto",
    padding: "clamp(16px, 4vw, 32px)",
    paddingBottom: "80px",
    fontFamily: "'Segoe UI', 'Inter', system-ui, sans-serif",
    backgroundColor: "#f7fafc",
    minHeight: "100vh",
    overflowY: "auto",  // Add this
    WebkitOverflowScrolling: "touch",  // Add this
  },

  offlineBanner: {
    background: "#ed8936",
    color: "#fff",
    padding: "12px",
    textAlign: "center",
    borderRadius: "8px",
    marginBottom: "20px",
    fontWeight: "500",
  },

  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    flexWrap: "wrap",
    gap: "16px",
    marginBottom: "24px",
  },

  title: {
    fontSize: "clamp(24px, 5vw, 32px)",
    fontWeight: "700",
    margin: "0 0 8px 0",
    color: "#2d3748",
  },

  subtitle: {
    fontSize: "14px",
    color: "#718096",
    margin: 0,
  },

  headerRight: {
    display: "flex",
    alignItems: "center",
    gap: "16px",
  },

  adminProfile: {
    cursor: "pointer",
  },

  adminAvatar: {
    width: "40px",
    height: "40px",
    borderRadius: "50%",
    background: "linear-gradient(135deg, #667eea, #764ba2)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "#fff",
    fontWeight: "bold",
    fontSize: "18px",
  },

  mobileMenu: {
    position: "absolute",
    top: "70px",
    right: "20px",
    backgroundColor: "#fff",
    borderRadius: "12px",
    boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
    zIndex: 1000,
    minWidth: "160px",
    overflow: "hidden",
  },

  mobileMenuItem: {
    padding: "12px 16px",
    display: "flex",
    alignItems: "center",
    gap: "12px",
    fontSize: "14px",
    color: "#2d3748",
    cursor: "pointer",
    transition: "background 0.2s ease",
    borderBottom: "1px solid #e2e8f0",
  },

  statsGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
    gap: "16px",
    marginBottom: "24px",
  },

  statCard: {
    backgroundColor: "#fff",
    padding: "16px",
    borderRadius: "12px",
    display: "flex",
    alignItems: "center",
    gap: "12px",
    boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
    border: "1px solid #e2e8f0",
  },

  statIcon: {
    fontSize: "32px",
  },

  statValue: {
    fontSize: "24px",
    fontWeight: "700",
    margin: 0,
    color: "#2d3748",
  },

  statLabel: {
    fontSize: "12px",
    color: "#718096",
    margin: 0,
  },

  alert: {
    padding: "12px 16px",
    borderRadius: "10px",
    marginBottom: "20px",
    fontSize: "14px",
    fontWeight: "500",
  },

  alertSuccess: {
    backgroundColor: "#c6f6d5",
    color: "#22543d",
    border: "1px solid #9ae6b4",
  },

  alertError: {
    backgroundColor: "#fed7d7",
    color: "#742a2a",
    border: "1px solid #fc8181",
  },

  formCard: {
    backgroundColor: "#fff",
    borderRadius: "16px",
    padding: "clamp(20px, 5vw, 32px)",
    marginBottom: "24px",
    boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
    border: "1px solid #e2e8f0",
  },

  formTitle: {
    fontSize: "clamp(18px, 4vw, 20px)",
    fontWeight: "600",
    margin: "0 0 20px 0",
    color: "#2d3748",
  },

  formGroup: {
    marginBottom: "20px",
  },

  label: {
    display: "block",
    marginBottom: "8px",
    fontWeight: "500",
    color: "#4a5568",
    fontSize: "14px",
  },

  optionalText: {
    fontSize: "12px",
    fontWeight: "normal",
    color: "#a0aec0",
  },

  hintText: {
    display: "block",
    marginTop: "5px",
    fontSize: "11px",
    color: "#a0aec0",
  },

  input: {
    width: "100%",
    padding: "12px 16px",
    borderRadius: "10px",
    border: "2px solid #e2e8f0",
    fontSize: "14px",
    transition: "all 0.3s ease",
    boxSizing: "border-box",
  },

  inputError: {
    borderColor: "#fc8181",
  },

  errorText: {
    display: "block",
    marginTop: "5px",
    fontSize: "12px",
    color: "#e53e3e",
  },

  buttonGroup: {
    display: "flex",
    gap: "12px",
    marginTop: "24px",
  },

  submitBtn: {
    flex: 1,
    padding: "12px 24px",
    backgroundColor: "#667eea",
    color: "#fff",
    border: "none",
    borderRadius: "10px",
    fontSize: "14px",
    fontWeight: "600",
    cursor: "pointer",
    transition: "all 0.3s ease",
  },

  cancelBtn: {
    padding: "12px 24px",
    backgroundColor: "#e2e8f0",
    color: "#4a5568",
    border: "none",
    borderRadius: "10px",
    fontSize: "14px",
    fontWeight: "600",
    cursor: "pointer",
    transition: "all 0.3s ease",
  },

  disabledBtn: {
    opacity: 0.6,
    cursor: "not-allowed",
  },

  loader: {
    display: "inline-block",
    width: "16px",
    height: "16px",
    border: "2px solid #fff",
    borderTop: "2px solid transparent",
    borderRadius: "50%",
    animation: "spin 0.8s linear infinite",
  },

  searchSection: {
    marginBottom: "24px",
  },

  searchWrapper: {
    position: "relative",
  },

  searchIcon: {
    position: "absolute",
    left: "14px",
    top: "50%",
    transform: "translateY(-50%)",
    fontSize: "16px",
    color: "#a0aec0",
  },

  searchInput: {
    width: "100%",
    padding: "12px 40px 12px 40px",
    borderRadius: "12px",
    border: "2px solid #e2e8f0",
    fontSize: "14px",
    transition: "all 0.3s ease",
    boxSizing: "border-box",
    backgroundColor: "#fff",
  },

  clearBtn: {
    position: "absolute",
    right: "12px",
    top: "50%",
    transform: "translateY(-50%)",
    background: "#e2e8f0",
    border: "none",
    borderRadius: "50%",
    width: "22px",
    height: "22px",
    cursor: "pointer",
    fontSize: "12px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },

  listCard: {
    backgroundColor: "#fff",
    borderRadius: "16px",
    overflow: "hidden",
    boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
    border: "1px solid #e2e8f0",
  },

  listHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "16px 20px",
    borderBottom: "1px solid #e2e8f0",
    backgroundColor: "#fafafa",
  },

  listTitle: {
    fontSize: "16px",
    fontWeight: "600",
    margin: 0,
    color: "#2d3748",
  },

  listCount: {
    fontSize: "13px",
    color: "#718096",
    backgroundColor: "#edf2f7",
    padding: "4px 10px",
    borderRadius: "20px",
  },

  tableWrapper: {
    overflowX: "auto",
  },

  table: {
    width: "100%",
    borderCollapse: "collapse",
  },

  tableHeader: {
    backgroundColor: "#f7fafc",
  },

  th: {
    textAlign: "left",
    padding: "14px 16px",
    fontSize: "13px",
    fontWeight: "600",
    color: "#4a5568",
    borderBottom: "1px solid #e2e8f0",
  },

  tableRow: {
    borderBottom: "1px solid #e2e8f0",
    transition: "background-color 0.2s ease",
  },

  td: {
    padding: "14px 16px",
    fontSize: "14px",
    color: "#2d3748",
  },

  teacherName: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
  },

  avatar: {
    width: "32px",
    height: "32px",
    borderRadius: "50%",
    backgroundColor: "#667eea",
    color: "#fff",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "14px",
    fontWeight: "600",
  },

  teacherId: {
    fontSize: "12px",
    backgroundColor: "#edf2f7",
    padding: "4px 8px",
    borderRadius: "6px",
    fontFamily: "monospace",
    fontWeight: "600",
  },

  mobileLink: {
    color: "#667eea",
    textDecoration: "none",
  },

  naText: {
    color: "#a0aec0",
    fontSize: "12px",
  },

  statusActive: {
    display: "inline-block",
    padding: "4px 10px",
    borderRadius: "20px",
    fontSize: "11px",
    fontWeight: "600",
    backgroundColor: "#c6f6d5",
    color: "#22543d",
  },

  statusInactive: {
    display: "inline-block",
    padding: "4px 10px",
    borderRadius: "20px",
    fontSize: "11px",
    fontWeight: "600",
    backgroundColor: "#fed7d7",
    color: "#742a2a",
  },

  actionButtons: {
    display: "flex",
    gap: "8px",
    flexWrap: "wrap",
  },

  editBtn: {
    padding: "6px 12px",
    backgroundColor: "#edf2f7",
    border: "none",
    borderRadius: "6px",
    fontSize: "12px",
    cursor: "pointer",
    transition: "all 0.2s ease",
    color: "#2d3748",
  },

  deleteBtn: {
    padding: "6px 12px",
    backgroundColor: "#fed7d7",
    border: "none",
    borderRadius: "6px",
    fontSize: "12px",
    cursor: "pointer",
    transition: "all 0.2s ease",
    color: "#742a2a",
  },

  loadingState: {
    textAlign: "center",
    padding: "60px 20px",
    color: "#718096",
  },

  spinner: {
    width: "40px",
    height: "40px",
    border: "3px solid #e2e8f0",
    borderTop: "3px solid #667eea",
    borderRadius: "50%",
    animation: "spin 0.8s linear infinite",
    margin: "0 auto 16px",
  },

  emptyState: {
    textAlign: "center",
    padding: "60px 20px",
    color: "#a0aec0",
  },

  emptyIcon: {
    fontSize: "48px",
    display: "block",
    marginBottom: "16px",
  },

  clearSearchBtn: {
    marginTop: "12px",
    padding: "8px 16px",
    backgroundColor: "#667eea",
    color: "#fff",
    border: "none",
    borderRadius: "8px",
    cursor: "pointer",
  },

  bottomNav: {
    position: "fixed",
    bottom: 0,
    left: 0,
    right: 0,
    backgroundColor: "#fff",
    display: "flex",
    justifyContent: "space-around",
    alignItems: "center",
    padding: "8px 16px",
    boxShadow: "0 -2px 10px rgba(0,0,0,0.1)",
    zIndex: 100,
    borderTop: "1px solid #e2e8f0",
  },

  bottomNavItem: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: "4px",
    cursor: "pointer",
    padding: "6px 12px",
    borderRadius: "8px",
    transition: "all 0.2s ease",
    flex: 1,
  },

  bottomNavItemActive: {
    color: "#667eea",
  },

  bottomNavIcon: {
    fontSize: "22px",
  },

  bottomNavLabel: {
    fontSize: "11px",
    fontWeight: "500",
  },
};

// Add animations
const styleSheet = document.createElement("style");
styleSheet.textContent = `
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
  
  input:focus {
    outline: none;
    border-color: #667eea;
    box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
  }
  
  button:hover:not(:disabled) {
    transform: translateY(-1px);
  }
  
  .edit-btn:hover {
    background-color: #e2e8f0;
  }
  
  .delete-btn:hover {
    background-color: #fc8181;
    color: #fff;
  }
  
  .submit-btn:hover:not(:disabled) {
    background-color: #5a67d8;
  }
  
  .cancel-btn:hover {
    background-color: #cbd5e0;
  }
  
  .table-row:hover {
    background-color: #f7fafc;
  }
  
  @media (max-width: 768px) {
    .action-buttons {
      flex-direction: column;
    }
    
    .edit-btn, .delete-btn {
      width: 100%;
    }
    
    th, td {
      padding: 10px 12px;
    }
    
    .teacher-id {
      font-size: 10px;
    }
    
    .stats-grid {
      grid-template-columns: 1fr;
    }
  }
`;
document.head.appendChild(styleSheet);